"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDetailedPriceElementsAsParts = exports.getDetailedPriceElements = exports.getCompactPriceDescription = exports.getPriceComponentAsParts = exports.getPriceComponentAsText = exports.filterPriceComponents = exports.filterPriceSegments = exports.formatAmountAsParts = void 0;
const tslib_1 = require("tslib");
const localization_1 = (0, tslib_1.__importStar)(require("../localization"));
const models_1 = require("../models");
const restrictions_1 = require("./restrictions");
const cleanAmount = (amount) => {
    return typeof amount === 'string'
        ? Number(amount.replace(/,/g, '.').replace(/\s/g, ''))
        : amount;
};
const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat((0, localization_1.getCurrencyLocale)(), {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
        maximumFractionDigits: 8,
        minimumFractionDigits: 2,
    }).format(cleanAmount(amount));
};
const formatAmountAsParts = (price, currency) => {
    var _a, _b, _c;
    const formattedAmount = formatAmount(price, currency);
    const before = (_a = formattedAmount.match(/^[^0-9]*/)) === null || _a === void 0 ? void 0 : _a[0].trim();
    const after = (_b = formattedAmount.match(/[^0-9]*$/)) === null || _b === void 0 ? void 0 : _b[0].trim();
    const amount = ((_c = formattedAmount
        .match(/[-]{0,1}[\d.,\s]+/g)) === null || _c === void 0 ? void 0 : _c.join('').trim()) || '';
    return {
        before,
        amount,
        after,
    };
};
exports.formatAmountAsParts = formatAmountAsParts;
/*
    Displaying minute as the smallest unit and rounding to nearest minute results in sometimes ignoring parts of the price:
    1. For price elements with max_duration < 30 seconds (rounded to 0 min) the entire price element should be ignored and thus omitted entirely from the formatted display price.
    2. For price elements with both min_duration and max_duration where the difference is < 30 seconds (rounded to 0 min), the entire price element should be omitted.
*/
const filterPriceSegments = (priceSegments) => {
    const filteredPriceSements = priceSegments.filter((segment) => {
        var _a, _b, _c, _d, _e;
        if (((_a = segment.restrictions) === null || _a === void 0 ? void 0 : _a.max_duration) &&
            ((_b = segment.restrictions) === null || _b === void 0 ? void 0 : _b.max_duration) < 30) {
            return false;
        }
        if (((_c = segment.restrictions) === null || _c === void 0 ? void 0 : _c.max_duration) &&
            ((_d = segment.restrictions) === null || _d === void 0 ? void 0 : _d.min_duration) &&
            segment.restrictions.max_duration - ((_e = segment.restrictions) === null || _e === void 0 ? void 0 : _e.min_duration) <
                30) {
            return false;
        }
        return true;
    });
    return filteredPriceSements;
};
exports.filterPriceSegments = filterPriceSegments;
/*
   Pricing rules for PARKING_TIME:
   1. if we have PARKING_TIME + TIME and TIME is > 0 we only use the TIME component to price
   2. if we have PARKING_TIME + TIME and TIME is 0 we convert PARKING_TIME to TIME and price
   3. if we only have PARKING_TIME we convert it to TIME and price
*/
const filterPriceComponents = (priceComponents) => {
    var _a;
    const containsParkingTimeComponent = priceComponents.some((priceComponent) => priceComponent.type === 'PARKING_TIME');
    if (containsParkingTimeComponent) {
        const timeComponentPrice = (_a = priceComponents.find((priceComponent) => priceComponent.type === models_1.TariffDimensionType.TIME)) === null || _a === void 0 ? void 0 : _a.price;
        if (timeComponentPrice) {
            return priceComponents.filter((priceComponent) => priceComponent.type !== 'PARKING_TIME');
        }
        else {
            return priceComponents
                .filter((priceComponent) => priceComponent.type !== models_1.TariffDimensionType.TIME)
                .map((priceComponent) => {
                if (priceComponent.type === 'PARKING_TIME') {
                    return Object.assign(Object.assign({}, priceComponent), { type: models_1.TariffDimensionType.TIME });
                }
                return priceComponent;
            });
        }
    }
    return priceComponents;
};
exports.filterPriceComponents = filterPriceComponents;
const getUnit = ({ price, type, step_size }) => {
    if (price === 0) {
        return '';
    }
    switch (type) {
        case models_1.TariffDimensionType.FLAT:
            return localization_1.default.t('session');
        case models_1.TariffDimensionType.ENERGY:
            return 'kWh';
        case models_1.TariffDimensionType.TIME:
            if (step_size === 60 * 60) {
                return 'h';
            }
            if (step_size <= 60) {
                return 'min';
            }
            return `${step_size / 60} min`;
        default:
            return '';
    }
};
const getAdjustedPrice = ({ price, type, step_size }) => {
    if (type === models_1.TariffDimensionType.TIME) {
        const pricePerSecond = price / 3600;
        // Format step sizes under 60 (e.g. price per second) to price per minute.
        const stepSizeAdjustedPrice = step_size < 60 ? pricePerSecond * 60 : pricePerSecond * step_size;
        return Math.round(stepSizeAdjustedPrice * 100) / 100;
    }
    return price;
};
const getPriceComponentAsText = ({ priceComponent, currency, }) => {
    try {
        const { price } = priceComponent;
        if (price === 0) {
            return localization_1.default.t('freeOfCharge');
        }
        const adjustedPrice = getAdjustedPrice(priceComponent);
        return formatAmount(adjustedPrice, currency);
    }
    catch (e) {
        return '';
    }
};
exports.getPriceComponentAsText = getPriceComponentAsText;
const getPriceComponentAsParts = ({ priceComponent, currency, }) => {
    try {
        const { price } = priceComponent;
        if (price === 0) {
            return {
                before: '',
                amount: localization_1.default.t('freeOfCharge'),
                after: '',
            };
        }
        const adjustedPrice = getAdjustedPrice(priceComponent);
        const { before, amount, after } = (0, exports.formatAmountAsParts)(adjustedPrice, currency);
        return {
            before: before || '',
            amount: `${before ? ' ' : ''}${amount}${after ? ' ' : ''}`,
            after: after || '',
        };
    }
    catch (e) {
        return {
            before: '',
            amount: '',
            after: '',
        };
    }
};
exports.getPriceComponentAsParts = getPriceComponentAsParts;
const getCompactPriceComponentAsText = ({ priceComponent, currency, }) => {
    const { type } = priceComponent;
    const adjustedPrice = getAdjustedPrice(priceComponent);
    const { before, amount, after } = (0, exports.formatAmountAsParts)(adjustedPrice, currency);
    const shortAmount = amount.endsWith('.00') || amount.endsWith(',00')
        ? amount.substring(0, amount.length - 3)
        : amount;
    const compactPrice = `${before} ${shortAmount} ${after}`.trim();
    if (type === models_1.TariffDimensionType.FLAT) {
        return compactPrice;
    }
    const unit = getUnit(priceComponent);
    return `${compactPrice}${unit !== '' ? '/' : ''}${unit}`;
};
// Generate compact price description from price components
const getCompactPriceDescription = ({ content, currency, language, currencyLocale, }) => {
    try {
        (0, localization_1.adjustLanguage)(language, currencyLocale);
        const segments = (0, exports.filterPriceSegments)(content.segments);
        const profileDescription = segments.map(({ restrictions, price_components }) => {
            const priceRestrictions = (0, restrictions_1.getPriceRestrictions)({
                restrictions,
                compact: true,
                language,
                // There is no need to expose these values to the end user
                excludedRestrictions: ['start_date', 'end_date'],
            });
            const priceDescription = (0, exports.filterPriceComponents)(price_components)
                .filter(({ price }) => 
            // Displaying a price component with a value of 0 might lead to confusion.
            // https://plugsurfing.atlassian.net/browse/PRICE-62
            (price_components.length === 1 && priceRestrictions.length > 0) ||
                price > 0)
                .map((priceComponent) => getCompactPriceComponentAsText({ priceComponent, currency }))
                .join(' + ');
            const restrictionDescription = priceRestrictions.length > 0
                ? ` (${priceRestrictions.join(', ')})`
                : '';
            return `${priceDescription}${restrictionDescription}`;
        });
        return profileDescription.filter((profile) => profile !== '').join(', ');
    }
    catch (e) {
        return '';
    }
};
exports.getCompactPriceDescription = getCompactPriceDescription;
const getDetailedPriceElements = ({ content, currency, compact = false, priceComponentAsParts = false, language, currencyLocale, excludedRestrictions, }) => {
    try {
        (0, localization_1.adjustLanguage)(language, currencyLocale);
        const segments = (0, exports.filterPriceSegments)(content.segments);
        return segments.map(({ price_components, restrictions }) => ({
            priceComponents: (0, exports.filterPriceComponents)(price_components).map((priceComponent) => ({
                price: priceComponentAsParts
                    ? (0, exports.getPriceComponentAsParts)({ priceComponent, currency })
                    : (0, exports.getPriceComponentAsText)({ priceComponent, currency }),
                unit: getUnit(priceComponent),
                type: priceComponent.type,
            })),
            restrictions: (0, restrictions_1.getPriceRestrictions)({
                restrictions,
                language,
                compact,
                excludedRestrictions,
            }),
        }));
    }
    catch (e) {
        return [];
    }
};
exports.getDetailedPriceElements = getDetailedPriceElements;
const getDetailedPriceElementsAsParts = (params) => (0, exports.getDetailedPriceElements)(Object.assign(Object.assign({}, params), { priceComponentAsParts: true }));
exports.getDetailedPriceElementsAsParts = getDetailedPriceElementsAsParts;
